import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import api from '../lib/api';
import { autoPromptOnFirstLogin, bindResubscribeListener } from '../lib/push';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Callback set by E2EProvider to hook into login/logout events without introducing a circular provider dep.
  const e2eHookRef = useRef({ onLogin: null, onLogout: null });

  const registerE2EHooks = useCallback((hooks) => {
    e2eHookRef.current = hooks || {};
  }, []);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('gtc_token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      localStorage.removeItem('gtc_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  // Bind SW re-subscribe listener once and auto-prompt push on login
  useEffect(() => {
    const unbind = bindResubscribeListener();
    return unbind;
  }, []);

  useEffect(() => {
    if (user && !user.must_change_password) {
      // Fire and forget — silent if permission already answered or unsupported
      autoPromptOnFirstLogin().catch(() => {});
    }
  }, [user]);

  const login = async (username, password) => {
    const { data } = await api.post('/auth/login', { username, password });
    if (data.requires_2fa) {
      // 2FA flow — E2E init happens after 2FA success (see complete2FA)
      return data;
    }
    localStorage.setItem('gtc_token', data.token);
    setUser(data.user);
    // Kick off E2E init with the freshly-known password (never stored)
    if (e2eHookRef.current.onLogin) {
      e2eHookRef.current.onLogin(password).catch((err) => console.warn('[E2E init]', err));
    }
    return data.user;
  };

  const complete2FA = async (tmpToken, code) => {
    const { data } = await api.post('/auth/2fa/verify', { tmp_token: tmpToken, code });
    localStorage.setItem('gtc_token', data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    if (e2eHookRef.current.onLogout) {
      try { e2eHookRef.current.onLogout(); }
      catch (e) { console.warn('[Auth] onLogout hook failed:', e?.message || e); }
    }
    localStorage.removeItem('gtc_token');
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, complete2FA, logout, refresh: loadUser, registerE2EHooks }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
